import { useEffect, useMemo, useState } from "react";
import { Skeleton, Typography } from "@/components/atoms";
import { DataTable, DataTableSorting, Pagination } from "@/components/molecules";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGetBlockedDriversQuery } from "@/redux/apis/clientDispatch/clientDispatchApi";
import { BlockedDriversEmptyState } from "./components/BlockedDriversEmptyState";
import { blockedDriversColumns } from "./fixtures";
import { generateBlockedDriversData } from "./helpers";

export const BlockedDriversTable = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sorting, setSorting] = useState<DataTableSorting>([]);

  const { accountId } = useClientScope();

  const sort = sorting[0];
  const { data, isLoading } = useGetBlockedDriversQuery({
    page,
    pageSize,
    sort: sort?.id,
    order: sort?.desc ? "desc" : "asc",
    accountId,
  });

  // Reset to the first page whenever the sort changes (server re-sorts from page 1).
  useEffect(() => setPage(1), [sorting]);

  const totalItems = data?.totalItems ?? 0;
  const pageCount = data?.pageCount ?? 1;
  const currentPage = data?.page ?? page;

  const tableData = useMemo(() => generateBlockedDriversData(data?.items ?? [], accountId), [data, accountId]);

  if (isLoading) return <Skeleton />;

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-lg border border-neutral-mid-gray">
        <DataTable
          className="w-full overflow-x-auto !rounded-none !ring-0 [&_table]:table-auto [&_tr]:bg-white"
          data={tableData}
          columns={blockedDriversColumns}
          sorting={sorting}
          setSorting={setSorting}
          emptyState={<BlockedDriversEmptyState />}
        />

        {totalItems > 0 && (
          <div className="flex items-center border-t border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
            <Typography variant="small" className="whitespace-nowrap text-neutral-silver-gray">
              {rangeStart}–{rangeEnd} of {totalItems}
            </Typography>
          </div>
        )}
      </div>

      {totalItems > 0 && (
        <Pagination
          className="!mt-0"
          currentPage={currentPage}
          pageCount={pageCount}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageClick={setPage}
          onSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}
    </div>
  );
};
